import { formatMoney } from '@lombok/shared';
import type { AgingBucket, AgingBuckets, ArAgingItem } from '@lombok/api-client';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@lombok/ui';
import { PageHeader } from '@/components/PageHeader';
import { StatusPill, orderStatusLabel } from '@/components/StatusPill';
import { QueryState } from '@/components/QueryState';
import { useAccountNames, useArAging, useReportsDashboard } from '@/data/hooks';
import { downloadCsv } from '@/lib/csv';
import { invoiceNumberLabel } from '@/features/invoices/labels';
import {
  AGING_BUCKET_LABELS_ES,
  AGING_BUCKET_ORDER,
  DASHBOARD_ORDER_STATUS_ORDER,
  dueDaysLabel,
} from './labels';
import { toCsv } from './csv';

/**
 * Reportes (finance/admin). A read-only operational snapshot composed from the
 * reports backend: AR aging totals/buckets + per-invoice detail, pending-payments,
 * orders-by-status, and a low-stock count. Money is integer minor USD throughout,
 * rendered via the shared `formatMoney`. Desktop-dense per docs/11; the AR detail
 * is a table on desktop and cards on mobile.
 *
 * Authorization is enforced by the backend (non-finance roles get 403); the route
 * already lives in the finance/admin group, so this surface assumes access.
 *
 * Figures are CAPPED/approximate by design (see reportService) — surfaced as a
 * footnote so finance reads them as a snapshot, not an exact ledger.
 */
export function ReportesPage() {
  const dashboard = useReportsDashboard();
  const aging = useArAging();

  const items = aging.data?.items ?? [];
  const names = useAccountNames();
  const customerName = (id: string) => names.data?.get(id)?.name;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Reportes"
        description="Resumen financiero y operativo. Cifras aproximadas (instantánea)."
        actions={
          <ExportCsvButton items={items} customerName={customerName} disabled={aging.isLoading || aging.isError} />
        }
      />

      {/* KPI cards — sourced from the dashboard snapshot. */}
      <DashboardKpis query={dashboard} />

      {/* Antigüedad de saldos (AR aging): buckets summary + detail. */}
      <Card className="overflow-hidden">
        <CardHeader className="flex flex-col gap-1">
          <CardTitle>Antigüedad de saldos</CardTitle>
          <p className="text-sm text-text-muted">
            Saldo abierto por tramo de vencimiento (USD).
          </p>
        </CardHeader>
        <CardContent className="flex flex-col gap-5">
          <AgingBucketsSummary
            buckets={aging.data?.buckets}
            total={aging.data?.totalOpenMinorUsd}
            loading={aging.isLoading}
          />

          <div className="overflow-hidden rounded-md border border-border">
            <QueryState
              isLoading={aging.isLoading}
              isError={aging.isError}
              error={aging.error}
              onRetry={() => void aging.refetch()}
              isEmpty={items.length === 0}
              loadingLabel="Cargando antigüedad de saldos…"
              emptyTitle="Sin saldos abiertos"
              emptyDescription="No hay facturas con saldo pendiente en este momento."
            >
              {/* Desktop table */}
              <div className="hidden md:block">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-border text-xs uppercase tracking-wide text-text-muted">
                      <th className="px-4 py-3 font-medium">Factura</th>
                      <th className="px-4 py-3 font-medium">Cliente</th>
                      <th className="px-4 py-3 font-medium">Vence</th>
                      <th className="px-4 py-3 text-right font-medium">Saldo</th>
                      <th className="px-4 py-3 font-medium">Plazo</th>
                      <th className="px-4 py-3 font-medium">Tramo</th>
                      <th className="px-4 py-3 font-medium">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => (
                      <tr
                        key={it.invoiceId}
                        className="border-b border-border last:border-0 hover:bg-surface-muted"
                      >
                        <td className="px-4 py-3 font-mono text-brand-primary">{invoiceNumberLabel(it)}</td>
                        <td className="px-4 py-3 text-text-muted">{customerName(it.customerId) ?? it.customerId}</td>
                        <td className="px-4 py-3 text-text-muted">{formatDate(it.dueDate)}</td>
                        <td className="px-4 py-3 text-right font-semibold tabular-nums text-text-primary">
                          {formatMoney(it.balanceMinorUsd)}
                        </td>
                        <td className="px-4 py-3 text-text-muted">{dueDaysLabel(it.ageDays)}</td>
                        <td className="px-4 py-3">
                          <BucketBadge bucket={it.bucket} />
                        </td>
                        <td className="px-4 py-3">
                          <StatusPill kind="invoice" value={it.status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <ul className="divide-y divide-border md:hidden">
                {items.map((it) => (
                  <li key={it.invoiceId} className="flex flex-col gap-2 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <span className="flex flex-col">
                        <span className="font-mono text-sm text-brand-primary">{invoiceNumberLabel(it)}</span>
                        <span className="text-xs text-text-muted">{customerName(it.customerId) ?? it.customerId}</span>
                      </span>
                      <StatusPill kind="invoice" value={it.status} />
                    </div>
                    <div className="flex items-end justify-between text-sm">
                      <span className="flex flex-col gap-1">
                        <span className="text-text-muted">Vence {formatDate(it.dueDate)}</span>
                        <BucketBadge bucket={it.bucket} />
                      </span>
                      <span className="flex flex-col items-end">
                        <span className="text-xs text-text-muted">{dueDaysLabel(it.ageDays)}</span>
                        <span className="font-semibold text-text-primary">
                          {formatMoney(it.balanceMinorUsd)}
                        </span>
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </QueryState>
          </div>
        </CardContent>
      </Card>

      {/* Ventas por estado: orders-by-status counts. */}
      <Card>
        <CardHeader>
          <CardTitle>Ventas por estado</CardTitle>
        </CardHeader>
        <CardContent>
          <OrdersByStatus query={dashboard} />
        </CardContent>
      </Card>

      <GeneratedAtNote generatedAt={dashboard.data?.generatedAt} />
    </div>
  );
}

/* --------------------------------- KPIs ----------------------------------- */

function DashboardKpis({ query }: { query: ReturnType<typeof useReportsDashboard> }) {
  const d = query.data;
  const loading = query.isLoading;

  // On error we still render the cards (with em-dashes) so the page layout holds;
  // the AR detail panel below carries the retry affordance.
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <KpiCard
        label="Cuentas por cobrar"
        value={money(d?.ar.totalOpenMinorUsd, loading)}
        hint={
          d
            ? `${d.ar.openInvoiceCount} ${d.ar.openInvoiceCount === 1 ? 'factura abierta' : 'facturas abiertas'}`
            : undefined
        }
        tone="brand"
      />
      <KpiCard
        label="Pagos por revisar"
        value={count(d?.payments.pendingCount, loading)}
        hint={d ? formatMoney(d.payments.pendingMinorUsd) : undefined}
        tone={d && d.payments.pendingCount > 0 ? 'warning' : 'neutral'}
      />
      <KpiCard
        label="Inventario bajo"
        value={count(d?.inventory.lowStockCount, loading)}
        hint="Artículos en/bajo punto de reorden"
        tone={d && d.inventory.lowStockCount > 0 ? 'warning' : 'neutral'}
      />
      <KpiCard
        label="Pedidos pendientes de aprobación"
        value={count(d?.orders.byStatus.pending_approval, loading)}
        hint="Esperan revisión"
        tone={d && (d.orders.byStatus.pending_approval ?? 0) > 0 ? 'warning' : 'neutral'}
      />
    </div>
  );
}

type KpiTone = 'brand' | 'warning' | 'neutral';

function KpiCard({
  label,
  value,
  hint,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: KpiTone;
}) {
  const valueClass =
    tone === 'brand'
      ? 'text-brand-primary'
      : tone === 'warning'
        ? 'text-warning'
        : 'text-text-primary';
  return (
    <Card className="h-full">
      <CardContent className="pt-5">
        <p className="text-sm text-text-muted">{label}</p>
        <p className={`mt-1 text-2xl font-semibold tracking-tight tabular-nums ${valueClass}`}>
          {value}
        </p>
        {hint ? <p className="mt-1 text-xs text-text-muted">{hint}</p> : null}
      </CardContent>
    </Card>
  );
}

/* ----------------------------- AR aging buckets --------------------------- */

function AgingBucketsSummary({
  buckets,
  total,
  loading,
}: {
  buckets: AgingBuckets | undefined;
  total: number | undefined;
  loading: boolean;
}) {
  // Use the explicit total so the percentages match the headline AR figure even
  // when the detail list is capped relative to the rollup.
  const denom = total ?? 0;
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {AGING_BUCKET_ORDER.map((bucket) => {
        const amount = buckets?.[bucket] ?? 0;
        const pct = denom > 0 ? Math.round((amount / denom) * 100) : 0;
        return (
          <div key={bucket} className="rounded-md border border-border bg-surface p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium text-text-muted">
                {AGING_BUCKET_LABELS_ES[bucket]}
              </span>
              <span className="text-xs tabular-nums text-text-muted">
                {loading ? '—' : `${pct}%`}
              </span>
            </div>
            <p className="mt-1 text-lg font-semibold tabular-nums text-text-primary">
              {loading ? '…' : formatMoney(amount)}
            </p>
            {/* Share-of-total bar. */}
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-surface-muted">
              <div
                className={`h-full rounded-full ${BUCKET_BAR_CLASS[bucket]}`}
                style={{ width: `${loading ? 0 : pct}%` }}
                aria-hidden="true"
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Bar fill color per bucket — current (calm) → overdue (alarming). */
const BUCKET_BAR_CLASS: Record<AgingBucket, string> = {
  current: 'bg-success',
  d1_30: 'bg-info',
  d31_60: 'bg-warning',
  d60_plus: 'bg-danger',
};

/** Aging bucket pill, color-matched to the bucket's bar. */
function BucketBadge({ bucket }: { bucket: AgingBucket }) {
  const cls: Record<AgingBucket, string> = {
    current: 'bg-success-soft text-success',
    d1_30: 'bg-info-soft text-info',
    d31_60: 'bg-warning-soft text-warning',
    d60_plus: 'bg-danger-soft text-danger',
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cls[bucket]}`}
    >
      {AGING_BUCKET_LABELS_ES[bucket]}
    </span>
  );
}

/* ----------------------------- Orders by status --------------------------- */

function OrdersByStatus({ query }: { query: ReturnType<typeof useReportsDashboard> }) {
  return (
    <QueryState
      isLoading={query.isLoading}
      isError={query.isError}
      error={query.error}
      onRetry={() => void query.refetch()}
      loadingLabel="Cargando pedidos por estado…"
    >
      <OrdersByStatusBars byStatus={query.data?.orders.byStatus ?? {}} />
    </QueryState>
  );
}

function OrdersByStatusBars({ byStatus }: { byStatus: Record<string, number> }) {
  const rows = DASHBOARD_ORDER_STATUS_ORDER.map((status) => ({
    status,
    label: orderStatusLabel(status),
    count: byStatus[status] ?? 0,
  }));
  const max = Math.max(1, ...rows.map((r) => r.count));
  const total = rows.reduce((sum, r) => sum + r.count, 0);

  if (total === 0) {
    return (
      <p className="py-4 text-sm text-text-muted">No hay pedidos en estos estados.</p>
    );
  }

  return (
    <ul className="flex flex-col gap-3">
      {rows.map((r) => (
        <li key={r.status} className="flex items-center gap-3">
          <span className="w-44 shrink-0 text-sm text-text-primary">{r.label}</span>
          <span className="h-2.5 flex-1 overflow-hidden rounded-full bg-surface-muted">
            <span
              className="block h-full rounded-full bg-brand-primary"
              style={{ width: `${Math.round((r.count / max) * 100)}%` }}
              aria-hidden="true"
            />
          </span>
          <span className="w-10 shrink-0 text-right text-sm font-semibold tabular-nums text-text-primary">
            {r.count}
          </span>
        </li>
      ))}
    </ul>
  );
}

/* --------------------------------- Export --------------------------------- */

function ExportCsvButton({
  items,
  customerName,
  disabled,
}: {
  items: readonly ArAgingItem[];
  customerName: (customerId: string) => string | undefined;
  disabled: boolean;
}) {
  const onExport = () =>
    downloadCsv(`antiguedad-saldos-${new Date().toISOString().slice(0, 10)}.csv`, toCsv(items, customerName));

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={onExport}
      disabled={disabled || items.length === 0}
    >
      Exportar CSV
    </Button>
  );
}

/* --------------------------------- helpers -------------------------------- */

function GeneratedAtNote({ generatedAt }: { generatedAt?: string }) {
  return (
    <p className="text-xs text-text-muted">
      Cifras aproximadas: son una instantánea operativa, no un libro contable exacto.
      {generatedAt ? ` Generado el ${formatDateTime(generatedAt)}` : ''}
    </p>
  );
}

/** Count value: spinner ellipsis while loading, em-dash when absent. */
function count(n: number | undefined, loading: boolean): string {
  if (loading) return '…';
  if (n === undefined) return '—';
  return String(n);
}

/** Money value (minor USD): ellipsis while loading, em-dash when absent. */
function money(minor: number | undefined, loading: boolean): string {
  if (loading) return '…';
  if (minor === undefined) return '—';
  return formatMoney(minor);
}

/** Compact local date (es-VE); raw value fallback if unparseable. */
function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('es-VE', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** Local date + time (es-VE); raw value fallback if unparseable. */
function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString('es-VE', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
}
